import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Award, CheckCircle2, Download, RotateCcw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fetcher } from "@/lib/api";
import { downloadCertificate } from "@/lib/certificate";

export const TourQuiz = ({ open, onClose, onPassed }) => {
  const { data } = useSWR(open ? "/training/quiz" : null, fetcher);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = data?.items || [];
  const complete = items.length > 0 && items.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/training/quiz", { answers });
      setResult(r);
      if (r.passed) {
        toast.success(`Evaluación aprobada: ${r.score} de ${r.total}`);
        onPassed?.();
        downloadCertificate();
      } else {
        toast.error(`${r.score} de ${r.total}. Necesita ${r.pass_score} para aprobar.`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="grid-cols-1 w-[calc(100vw-1.5rem)] sm:w-full max-w-2xl max-h-[88vh] overflow-y-auto"
                     data-testid="quiz-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />Evaluación interna
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <>
            <p className="text-sm text-muted-foreground">
              {items.length} preguntas sobre el recorrido. Se aprueba con{" "}
              {data?.pass_score ?? 8} respuestas correctas y se puede repetir.
            </p>
            <div className="space-y-5 mt-2">
              {items.map((q, n) => (
                <div key={q.id} data-testid={`quiz-q-${q.id}`}>
                  <p className="text-sm font-medium">{n + 1}. {q.question}</p>
                  <div className="mt-2 space-y-1.5">
                    {q.options.map((opt, i) => (
                      <button
                        key={opt}
                        onClick={() => setAnswers({ ...answers, [q.id]: i })}
                        className={`w-full text-left text-sm px-3 py-2 border transition-colors ${
                          answers[q.id] === i
                            ? "border-primary bg-primary/10 text-foreground"
                            : "border-border hover:bg-muted/60 text-muted-foreground"
                        }`}
                        data-testid={`quiz-${q.id}-opt-${i}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
              <span className="text-xs text-muted-foreground" data-testid="quiz-progress">
                {Object.keys(answers).length} de {items.length} contestadas
              </span>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={onClose} data-testid="quiz-later">
                  Lo hago después
                </Button>
                <Button onClick={submit} disabled={!complete || busy} data-testid="quiz-submit">
                  {busy ? "Calificando…" : "Enviar respuestas"}
                </Button>
              </div>
            </div>
          </>
        )}

        {result && (
          <div data-testid="quiz-result">
            <div className="flex items-center gap-3">
              {result.passed
                ? <CheckCircle2 className="h-8 w-8 text-primary" />
                : <XCircle className="h-8 w-8 text-destructive" />}
              <div>
                <p className="text-lg font-semibold">
                  {result.score} de {result.total} correctas
                </p>
                <p className="text-sm text-muted-foreground">
                  {result.passed
                    ? `Aprobada. Su certificado se descargó automáticamente${result.folio ? ` (folio ${result.folio})` : ""}.`
                    : `Necesita ${result.pass_score} correctas. Repase y vuelva a intentarlo.`}
                </p>
              </div>
            </div>
            {(result.wrong || []).length > 0 && (
              <div className="mt-5 space-y-3">
                <div className="kicker">Para repasar</div>
                {result.wrong.map((w) => (
                  <div key={w.id} className="border-l-2 border-destructive pl-3">
                    <p className="text-sm font-medium">{w.question}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Correcta: <b className="text-foreground">{w.correct}</b>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{w.explain}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2 mt-6">
              {result.passed && (
                <Button variant="outline" onClick={() => downloadCertificate()}
                        data-testid="quiz-certificate">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Descargar certificado
                </Button>
              )}
              {!result.passed && (
                <Button variant="outline" onClick={retry} data-testid="quiz-retry">
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Intentar otra vez
                </Button>
              )}
              <Button onClick={onClose} data-testid="quiz-close">Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
